import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { addNoteToMatter, deleteNoteFromMatter, fetchMatterNotes, showEditMatterDataModal, updateNoteInMatter } from '../../../store/actions/Matters/MatterActions';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import {
  PiNoteLight,
  PiPlusLight,
  PiPencilLight,
  PiTrashLight,
  PiTextAaLight,
  PiTextAlignLeftLight,
  PiHashLight,
  PiCalendarLight,
  PiToggleLeftLight,
  PiTagLight,
  PiSparkleLight
} from 'react-icons/pi';
import { motion } from 'framer-motion';
import { MatterData } from '../MatterDetails';
import clsx from 'clsx';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, notesItemsTourSteps } from '../../shared/FeatureTour';

export interface NoteItem {
  label: string;
  value: string;
  type: 'short_text' | 'long_text' | 'number' | 'date' | 'boolean';
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

interface NotesTabContentProps {
  matterData: MatterData | null;
  notes: NoteItem[];
  setNotes: React.Dispatch<React.SetStateAction<NoteItem[]>>;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  loading?: boolean;
  hasDocuments?: boolean;
}

const NotesTabContent: React.FC<NotesTabContentProps> = ({
  matterData,
  notes,
  setNotes,
  userAccessLevel,
  onUpdate,
  loading = false,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { matterId } = useParams<{ matterId: string }>();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const matterUuid = matterData?.uuid || matterData?.MatterID || matterId || '';
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchTerm, setSearchTerm] = useState('');

  const { isGenerating: isFindingMore, handleGenerateMore: handleFindMoreNotes, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'notes',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'notes-items',
    steps: notesItemsTourSteps,
    readyToShow: notes.length > 0 && !loading,
    autoStartDelay: 600,
  });

  const [isTabLoading, setIsTabLoading] = useState(loading);

  const refreshNotesData = async () => {
    if (!matterId) return;
    setIsTabLoading(true);
    try {
      const freshNotes = await dispatch(fetchMatterNotes(matterId));
      setNotes(freshNotes || []);
      onUpdate();
    } catch (error) {
      console.error('Failed to refresh notes:', error);
    } finally {
      setIsTabLoading(false);
    }
  };

  useEffect(() => {
    setIsTabLoading(loading);
  }, [loading]);


  // Safely convert any note value (string, array, object) to a displayable string
  const safeStringValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      return value.map((item) => {
        if (typeof item === 'object' && item !== null) {
          // e.g. {name: "Emily", role: "petitioner"} → "Emily (petitioner)"
          if (item.name && item.role) return `${item.name} (${item.role})`;
          if (item.name) return item.name;
          return Object.values(item).join(': ');
        }
        return String(item);
      }).join(', ');
    }
    if (typeof value === 'object') {
      // e.g. {date_of_marriage: "2012-07-15", ...} → "Date of marriage: 2012-07-15, ..."
      return Object.entries(value)
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
        .join(', ');
    }
    return String(value);
  };

  const detectValueType = (value: string): NoteItem['type'] => {
    if (!value) return 'short_text';
    const trimmedValue = value.trim();
    
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
      return 'date';
    }
    
    if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
      return 'number';
    }
    
    if (trimmedValue.toLowerCase() === 'true' || trimmedValue.toLowerCase() === 'false' || 
        trimmedValue.toLowerCase() === 'yes' || trimmedValue.toLowerCase() === 'no') {
      return 'boolean';
    }
    
    if (trimmedValue.length > 50) {
      return 'long_text';
    }
    
    return 'short_text';
  };

  const formatValue = (value: string, type: string) => {
    if (!value) return 'N/A';
    
    switch (type) {
      case 'date':
        try {
          const date = new Date(value);
          return date.toLocaleDateString('en-GB', { 
            year: 'numeric', 
            month: 'long', 
            day: '2-digit' 
          });
        } catch {
          return value;
        }
      case 'boolean':
        const lowerValue = value.toLowerCase();
        if (lowerValue === 'true' || lowerValue === 'yes') return t('Yes');
        if (lowerValue === 'false' || lowerValue === 'no') return t('No');
        return value;
      case 'number':
        return Number(value).toLocaleString();
      default:
        return value;
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'date': return <PiCalendarLight className="h-4 w-4 text-gray-500" />;
      case 'number': return <PiHashLight className="h-4 w-4 text-gray-500" />;
      case 'boolean': return <PiToggleLeftLight className="h-4 w-4 text-gray-500" />;
      case 'long_text': return <PiTextAlignLeftLight className="h-4 w-4 text-gray-500" />;
      default: return <PiTextAaLight className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleAddNote = () => {
    if (!canEdit) return;
    const matterUuid = matterData?.uuid || matterData?.MatterID || '';
    dispatch(showEditMatterDataModal(
      matterUuid,
      'notes',
      undefined,
      async (data: { label: string; value: string; type: string }) => {
        try {
          await dispatch(addNoteToMatter(matterUuid, data));
          dispatch(showFeedbackModal({
            modalType: 'success',
            message: t('Note added successfully'),
            showModal: true,
            duration: 3000
          }));
          await refreshNotesData();
          dispatch(hideModal())
        } catch (error) {
          console.error('Failed to add note:', error);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to add note'),
            showModal: true,
            duration: 3000
          }));
        }
      }
    ));
  };

  const handleEditNote = (note: NoteItem) => {
    if (!canEdit) return;
    const matterUuid = matterData?.uuid || matterData?.MatterID || '';
    // Normalize value to string and resolve type for the edit modal
    const normalizedValue = safeStringValue(note.value);
    const resolvedType = note.type && ['short_text', 'long_text', 'number', 'date', 'boolean'].includes(note.type)
      ? note.type
      : detectValueType(normalizedValue);
    const normalizedNote: NoteItem = {
      ...note,
      value: normalizedValue,
      type: resolvedType,
    };
    dispatch(showEditMatterDataModal(
      matterUuid,
      'notes',
      normalizedNote,
      async (data: { label: string; value: string; type: string; oldLabel?: string }) => {
        try {
          await dispatch(updateNoteInMatter(matterUuid, { 
            ...data, 
            oldLabel: note.label 
          }));
          // dispatch(showFeedbackModal({
          //   modalType: 'success',
          //   message: t('Note updated successfully'),
          //   showModal: true,
          //   duration: 3000
          // }));
          await refreshNotesData(); 
          dispatch(hideModal())
        } catch (error) {
          console.error('Failed to update note:', error);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to update note'),
            showModal: true,
            duration: 3000
          }));
        }
      }
    ));
  };

  const handleDeleteNote = (label: string) => {
    if (!canEdit) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Note'),
      subMsg: t('This note will be permanently deleted. This action cannot be undone.'),
      isDest: true,
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      onConfirm: async () => {
        if (!matterId) return;
        try {
          await dispatch(deleteNoteFromMatter(matterId, label));
          dispatch(showFeedbackModal({
            modalType: 'success',
            message: t('Note deleted successfully'),
            showModal: true,
            duration: 3000
          }));
          await refreshNotesData();
          dispatch(hideModal()) 
        } catch (error) {
          console.error('Failed to delete note:', error);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to delete note'),
            showModal: true,
            duration: 3000
          }));
        }
        dispatch(hideModal());
      },
      showModal: true,
    }));
  };

  const NoteItem: React.FC<{
    note: NoteItem;
    onEdit: (note: NoteItem) => void;
    onDelete: (label: string) => void;
    canEdit: boolean;
  }> = ({ note, onEdit, onDelete, canEdit }) => {
    const strValue = safeStringValue(note.value);
    const type = note.type || detectValueType(strValue);
    const formattedValue = formatValue(strValue, type);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="flex items-start p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-yellow-50 dark:bg-gray-800 transition-all hover:bg-yellow-100 dark:hover:bg-gray-700 w-full"
      >
        <div className="mr-3 flex-shrink-0">{getIconForType(type)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200 block truncate">
                {note.label}
              </span>
              <p className={clsx(
                "text-sm text-gray-600 dark:text-gray-300 mt-1 truncate",
                type === 'boolean' ? 'font-medium' : ''
              )}>
                {formattedValue}
              </p>
            </div>
            {canEdit && (
              <div className="flex space-x-2 ml-3 flex-shrink-0">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => onEdit(note)}
                  className="h-7 w-7 p-0"
                  title={t('Edit')}
                >
                  <PiPencilLight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="destructive"
                  size="small"
                  onClick={() => onDelete(note.label)}
                  className="h-7 w-7 p-0"
                  title={t('Delete')}
                >
                  <PiTrashLight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (isTabLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-48 rounded"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-24 rounded"></div>
        </div>
        <SkeletonClientItem viewMode="table" count={5} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        {t('Loading notes...')}
      </div>
    );
  }

  const filteredNotes = notes.filter((note) => {
    const term = searchTerm.toLowerCase();
    const labelMatch = (note.label || '').toLowerCase().includes(term);
    const valueStr = safeStringValue(note.value).toLowerCase();
    return labelMatch || valueStr.includes(term);
  });

  return (
    <div className="space-y-2">
      <div data-tour-id="notes-actions">
      <SubTabBar
        tabs={[{ id: 'notes', label: t('Notes'), icon: <PiTagLight className="w-3 h-3" /> }]}
        activeTab="notes"
        onTabChange={() => {}}
        actions={canEdit && !isFindingMore ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={handleAddNote}
            >
              {t('Add Note')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreNotes}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {notes.length === 0 ? t('Generate') : t('Find More')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {isFindingMore || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMore} />
      ) : (
      <div>
      <FilterBarWithTabs
        onSearchChange={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        noParentPadding={true}
        noParentHeight={true}
        itemCount={filteredNotes.length}
        totalItemCount={notes.length}
        itemCountLabel={notes.length === 1 ? t('note') : t('notes')}
        />

      {notes.length > 0 ? (
        viewMode === 'table' ? (
          <div data-tour-id="notes-table-area" className="overflow-x-auto">
            <Table
            columns={['Label', 'Value', 'Type']}
            data={filteredNotes.map((note, index) => {
              const strValue = safeStringValue(note.value);
              const detectedType = note.type || detectValueType(strValue);
              return {
                id: note.label,
                'Label': note.label || '',
                'Value': formatValue(strValue, detectedType) || t('N/A'),
                'Type': detectedType || 'short_text',
                _originalNote: note,
              };
            })}
            renderCell={(column: string, value: any, row: any) => {
              if (column === 'Type') {
                const typeValue = value || 'short_text';
                return (
                  <div className="flex items-center gap-2">
                    {getIconForType(typeValue)}
                    <span className="capitalize text-xs font-medium text-gray-700 dark:text-gray-300">
                      {typeValue === 'short_text' ? t('Short Text') :
                       typeValue === 'long_text' ? t('Long Text') :
                       typeValue === 'date' ? t('Date') :
                       typeValue === 'number' ? t('Number') :
                       typeValue === 'boolean' ? t('Yes / No') :
                       typeValue}
                    </span>
                  </div>
                );
              }
              if (column === 'Value') {
                return (
                  <div className="text-sm text-gray-900 dark:text-gray-200 truncate" title={value}>
                    {value}
                  </div>
                );
              }
              return undefined;
            }}
            onRowClick={(row) => {
              if (canEdit && row._originalNote) handleEditNote(row._originalNote);
            }}
            actions={canEdit ? [
              {
                label: t('Edit'),
                icon: <PiPencilLight className="h-4 w-4" />,
                onClick: (row) => {
                  if (row._originalNote) handleEditNote(row._originalNote);
                }
              },
              {
                label: t('Delete'),
                icon: <PiTrashLight className="h-4 w-4" />,
                onClick: (row) => handleDeleteNote(row.Label),
                isDestructive: true
              }
            ] : []}
              resizableColumns={true}
              max="max-h-[calc(100vh-200px)]"
              min="min-h-[calc(100vh-200px)]"
            />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 p-2 sm:grid-cols-1 gap-4 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar-main pb-2">
            {filteredNotes.map((note, index) => (
              <NoteItem
                key={`${note.label}-${index}`}
                note={note}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
                canEdit={canEdit}
              />
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12 rounded-lg">
          <PiNoteLight className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('No Notes Yet')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {t('Add structured notes to keep important information organized and easily accessible.')}
          </p>
          {canEdit && (
            <div className="flex items-center gap-2 justify-center">
              <Button
                variant="outline"
                size="small"
                onClick={handleAddNote}
                icon={<PiPlusLight className="h-4 w-4" />}
              >
                {t('Add Your First Note')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleFindMoreNotes}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
      )}
      <FeatureTour
        steps={notesItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default NotesTabContent;
